import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, Minus, Server } from 'lucide-react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal from '../../components/Reveal';
import {
  CheckItem, Faq, Icon, IconCards, Intro, QuoteSection, ScreenTour, SectionTitle, Steps, TrustStrip,
} from '../../components/product/ProductKit';
import { CLOUD_PBX as D } from '../../data/cloudPbx';
import { vatNote } from '../../config/vat';
import { useSiteContent, withProductContent } from '../../content/siteContent';

const Pricing = ({ onStartTrial }) => {
  return (
    <>
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
        {D.pricing.plans.map((p, i) => (
          <Reveal
            key={p.name}
            delay={i * 80}
            className={`relative flex flex-col rounded-3xl p-8 ${p.highlight ? 'bg-[#0a1f2e] text-white shadow-[0_30px_80px_-30px_rgba(10,31,46,0.6)] lg:-my-4 lg:py-12' : 'border border-gray-200 bg-white'}`}
          >
            {p.highlight && <span className="absolute -top-3 left-8 rounded-full bg-[#c8ff00] px-3 py-1 text-xs font-bold text-[#0a1f2e]">Most popular</span>}
            <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
            <p className={`mt-1 text-sm ${p.highlight ? 'text-gray-300' : 'text-gray-600'}`}>{p.tagline}</p>
            <div className="mt-6 flex items-end gap-1">
              <span className="font-display text-5xl font-semibold">£{p.monthly}</span>
              <span className={`mb-1.5 text-sm ${p.highlight ? 'text-gray-300' : 'text-gray-500'}`}>/user/month</span>
            </div>
            <p className={`mt-1 text-xs ${p.highlight ? 'text-gray-400' : 'text-gray-500'}`}>billed monthly on a 24-month term, {vatNote(true)}</p>
            <ul className={`mt-8 flex-1 space-y-3 text-sm ${p.highlight ? 'text-gray-200' : 'text-gray-700'}`}>
              {p.features.map((f) => <CheckItem key={f}>{f}</CheckItem>)}
            </ul>
            <button
              type="button"
              onClick={() => onStartTrial(p.name)}
              className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition active:scale-[0.98] ${p.highlight ? 'bg-[#c8ff00] text-[#0a1f2e] hover:brightness-95' : 'border border-[#0a1f2e]/25 text-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-white'}`}
            >
              14 Days Free Trial <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-12 rounded-2xl border border-gray-200 bg-[#f5f7f9] px-6 py-5">
        <p className="text-center text-sm font-semibold uppercase tracking-wider text-gray-500">Included in every plan</p>
        <ul className="mt-4 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm font-medium text-gray-700">
          {D.included.map((i) => <CheckItem key={i}>{i}</CheckItem>)}
        </ul>
      </Reveal>
      <p className="mx-auto mt-8 max-w-3xl text-center text-sm text-gray-500">{D.pricing.note}</p>
      <p className="mt-4 text-center">
        <a href="#compare" className="font-semibold underline decoration-[#c8ff00] decoration-2 underline-offset-4 hover:decoration-[#0a1f2e]">Compare every feature</a>
      </p>
    </>
  );
};

const Cell = ({ value }) => {
  if (value === true) {
    return (
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#c8ff00]" aria-label="Included">
        <Check className="h-3.5 w-3.5 text-[#0a1f2e]" strokeWidth={3} />
      </span>
    );
  }
  if (value === false) return <Minus className="inline h-4 w-4 text-gray-300" aria-label="Not included" />;
  return <span className="text-sm font-semibold">{value}</span>;
};

/** Full plan comparison table; the Business column is highlighted like its plan card. */
const Compare = () => (
  <Reveal className="overflow-x-auto rounded-3xl border border-gray-200 bg-white">
    <table className="w-full min-w-[640px] text-left">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="px-6 py-5 text-sm font-semibold text-gray-500">Feature</th>
          {D.compare.plans.map((name) => {
            const plan = D.pricing.plans.find((p) => p.name === name);
            return (
              <th key={name} className={`px-6 py-5 text-center ${plan?.highlight ? 'bg-[#0a1f2e] text-white' : ''}`}>
                <span className="block font-display text-lg font-semibold">{name}</span>
                {plan && <span className={`block text-xs font-medium ${plan.highlight ? 'text-gray-300' : 'text-gray-500'}`}>£{plan.monthly}/user/month</span>}
              </th>
            );
          })}
        </tr>
      </thead>
      {D.compare.groups.map((g) => (
        <tbody key={g.title}>
          <tr className="bg-[#f5f7f9]">
            <th colSpan={D.compare.plans.length + 1} className="px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">{g.title}</th>
          </tr>
          {g.rows.map(([label, ...values]) => (
            <tr key={label} className="border-t border-gray-100">
              <td className="px-6 py-3.5 text-sm text-gray-700">{label}</td>
              {values.map((v, i) => {
                const plan = D.pricing.plans.find((p) => p.name === D.compare.plans[i]);
                return <td key={i} className={`px-6 py-3.5 text-center ${plan?.highlight ? 'bg-[#0a1f2e]/[0.04]' : ''}`}><Cell value={v} /></td>;
              })}
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  </Reveal>
);

const CloudPBX = () => {
  const P = withProductContent(useSiteContent('cloud-pbx'), D);
  const navigate = useNavigate();
  // The full signup (plan, numbers, phones, details) lives on its own page.
  const startTrial = (plan = null) => navigate(`/free-trial${plan ? `?plan=${encodeURIComponent(plan)}` : ''}`);

  const hero = P.hero;

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo
        title="Cloud PBX: Hosted Business Phone System"
        description="VOIP2 Cloud PBX: a business phone system in the cloud with call routing, IVR, queues, apps for every device and simple per-user pricing."
        path="/products/cloud-pbx"
        breadcrumbs={[{ name: 'Cloud PBX', path: '/products/cloud-pbx' }]}
        jsonLd={[serviceLd({ name: 'Cloud PBX', description: "VOIP2 Cloud PBX: a business phone system in the cloud with call routing, IVR, queues, apps for every device and simple per-user pricing.", path: '/products/cloud-pbx' }), faqLd(D.faqs)]}
      />

      {/* Hero */}
      <section className="overflow-hidden px-4 pb-16 pt-28 sm:px-6 lg:px-8 lg:pt-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal as="h1" className="font-display text-5xl font-semibold leading-[1.08] tracking-tight md:text-6xl">
              {hero.title}{hero.highlight && <> <span className="whitespace-nowrap rounded-lg bg-[#c8ff00] px-2">{hero.highlight}</span></>}
            </Reveal>
            <Reveal as="p" delay={120} className="mt-6 max-w-[50ch] text-lg leading-relaxed text-gray-600">
              {hero.sub}
            </Reveal>
            <Reveal delay={200} as="ul" className="mt-6 grid max-w-md grid-cols-2 gap-x-6 gap-y-2 text-sm font-medium text-gray-700">
              {hero.bullets.map((b) => <CheckItem key={b}>{b}</CheckItem>)}
            </Reveal>
            <Reveal delay={280} className="mt-10 flex flex-col gap-4 sm:flex-row">
              <button type="button" onClick={() => startTrial()} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition hover:brightness-95 active:scale-[0.98]">
                14 Days Free Trial <ArrowRight className="h-5 w-5" />
              </button>
              <a href="#pricing" className="inline-flex items-center justify-center rounded-full border border-[#0a1f2e]/25 px-8 py-4 font-semibold transition-colors hover:border-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-white active:scale-[0.98]">
                See pricing
              </a>
            </Reveal>
          </div>
          <Reveal delay={150} className="relative lg:col-span-6">
            <div className="absolute inset-x-8 bottom-6 top-16 -z-10 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(200,255,0,0.35),transparent_65%)]" />
            <img src={hero.image} alt="VOIP2 Cloud PBX on a laptop, mobile app and IP desk phone" className="w-full" />
          </Reveal>
        </div>
      </section>

      <TrustStrip items={P.trust} />

      <Intro {...P.intro} />

      {/* Platform tour with screenshots */}
      <section id="platform" className="scroll-mt-20 bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.platform.title} sub={D.platform.sub} />
          <ScreenTour screens={D.platform.screens} />
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="scroll-mt-20 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.pricing.title} sub={D.pricing.sub} center />
          <Pricing onStartTrial={startTrial} />
        </div>
      </section>

      {/* Feature comparison */}
      <section id="compare" className="scroll-mt-20 bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <SectionTitle title={D.compare.title} sub={D.compare.sub} center />
          <Compare />
        </div>
      </section>

      {/* Add-ons */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.addons.title} sub={D.addons.sub} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {D.addons.items.map((a, i) => {
              const body = (
                <>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><Icon name={a.icon} className="h-5 w-5" /></span>
                  <h3 className="mt-4 flex items-center gap-1.5 font-display text-lg font-semibold">
                    {a.title} {a.to && <ArrowUpRight className="h-4 w-4 text-gray-400 transition-colors group-hover:text-[#0a1f2e]" />}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600">{a.body}</p>
                </>
              );
              return (
                <Reveal key={a.title} delay={(i % 3) * 70}>
                  {a.to ? (
                    <Link to={a.to} className="group block h-full rounded-2xl border border-gray-200 bg-white p-6 transition-colors hover:border-[#0a1f2e]">{body}</Link>
                  ) : (
                    <div className="h-full rounded-2xl border border-gray-200 bg-white p-6">{body}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Communicator */}
      <section className="bg-[#0a1f2e] px-4 py-24 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionTitle title={D.communicator.title} sub={D.communicator.sub} light />
              <Reveal as="ul" className="grid grid-cols-1 gap-3 text-gray-200 sm:grid-cols-2">
                {D.communicator.bullets.map((b) => <CheckItem key={b}>{b}</CheckItem>)}
              </Reveal>
            </div>
            <Reveal delay={120} className="lg:col-span-7">
              <img src={D.communicator.image} alt="VOIP2 mobile app: a call in progress and team chat with presence" loading="lazy" className="w-full" />
            </Reveal>
          </div>
          <div className="mt-16">
            <IconCards items={D.communicator.cards} columns={4} dark />
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.video.title} sub={D.video.sub} />
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="overflow-hidden rounded-3xl border border-gray-200 shadow-[0_30px_80px_-30px_rgba(10,31,46,0.35)]">
                <img src={D.video.image} alt="VOIP2 video meeting with screen sharing and live captions" loading="lazy" className="w-full" />
              </div>
            </Reveal>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1 xl:grid-cols-2">
              {D.video.features.map((f, i) => (
                <Reveal key={f.title} delay={(i % 2) * 70} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><Icon name={f.icon} className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-semibold">{f.title}</h3>
                    <p className="mt-1 text-sm text-gray-600">{f.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Numbers + devices */}
      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.numbers.title} sub={D.numbers.sub} />
          <Steps steps={D.numbers.steps} />

          <Reveal className="mt-20">
            <h3 className="font-display text-3xl font-semibold tracking-tight">{D.devices.title}</h3>
          </Reveal>
          <div className="mt-8">
            <IconCards items={D.devices.items} columns={4} />
          </div>
        </div>
      </section>

      {/* Reliability */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionTitle title={D.reliability.title} sub={D.reliability.sub} />
              <Reveal className="grid grid-cols-3 gap-6">
                {D.reliability.stats.map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-3xl font-semibold">{s.value}</div>
                    <div className="mt-1 text-sm text-gray-600">{s.label}</div>
                  </div>
                ))}
              </Reveal>
            </div>
            <div className="space-y-4 lg:col-span-7">
              {D.reliability.dcs.map((dc, i) => (
                <Reveal key={dc.city} delay={i * 90} className="flex items-start gap-5 rounded-2xl border border-gray-200 p-6">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0a1f2e] text-[#c8ff00]"><Server className="h-5 w-5" strokeWidth={1.75} /></span>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="font-display text-xl font-semibold">{dc.city}</h3>
                      <span className="text-sm text-gray-500">{dc.region}</span>
                    </div>
                    <p className="mt-1 text-gray-600">{dc.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CRM */}
      <section className="border-y border-gray-100 bg-[#f5f7f9] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionTitle title={D.crm.title} sub={D.crm.sub} center />
          <Reveal className="grid grid-cols-4 items-center gap-6 md:grid-cols-8">
            {D.crm.logos.map((l) => (
              <div key={l.alt} className="flex h-20 items-center justify-center rounded-2xl bg-white p-3">
                <img src={l.src} alt={l.alt} loading="lazy" className="max-h-14 w-auto object-contain" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <Faq faqs={D.faqs} />

      <QuoteSection
        title="Get a Cloud PBX quote"
        sub="Tell us about your team and we will send pricing and a setup plan within one working day."
        service="Cloud PBX"
        contextLabel="How many users?"
        contextOptions={D.userBands}
        related={D.related}
      />

    </div>
  );
};

export default CloudPBX;
